import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { KeyRound, Trash2, Save, ShieldCheck, ExternalLink } from "lucide-react";

export default function SettingsAI() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasKey, setHasKey] = useState(false);
  const [preview, setPreview] = useState("");
  const [newKey, setNewKey] = useState("");
  const [model, setModel] = useState("gpt-4o");
  const [availableModels, setAvailableModels] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/ai");
      setHasKey(data.has_api_key);
      setPreview(data.key_preview);
      setModel(data.model);
      setAvailableModels(data.available_models);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { model };
      if (newKey.trim()) payload.api_key = newKey.trim();
      await api.put("/settings/ai", payload);
      toast.success("Impostazioni salvate");
      setNewKey("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const clearKey = async () => {
    try {
      await api.put("/settings/ai", { api_key: "" });
      toast.success("Chiave rimossa");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const deleteAll = async () => {
    try {
      await api.delete("/settings/ai");
      toast.success("Configurazione AI resettata");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  if (loading) return <div className="p-10 text-slate-500">Caricamento…</div>;

  return (
    <div className="p-6 md:p-10 max-w-3xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-indigo-600 mb-2">Impostazioni AI</div>
          <h1 className="font-display font-black text-4xl md:text-5xl">Impostazioni AI</h1>
        <p className="text-slate-500 mt-2 text-sm max-w-2xl leading-relaxed">
           Configura la tua chiave API OpenAI, il modello e le istruzioni per generare le relazioni di audit. I costi verranno addebitati sul tuo account OpenAI.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="h-5 w-5 text-indigo-600" />
           <h2 className="font-display font-bold text-xl">Connessione OpenAI</h2>
          {hasKey && <span className="ml-auto text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">Configurata</span>}
        </div>

        {hasKey && (
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mb-4 flex items-center gap-3">
            <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Chiave salvata (cifrata)</div>
              <div className="font-mono-plex text-sm text-slate-900" data-testid="ai-key-preview">{preview}</div>
            </div>
            <Button variant="outline" size="sm" onClick={clearKey} data-testid="clear-key-btn" className="h-9 text-red-700 hover:bg-red-50 border-red-300">
              <Trash2 className="h-3.5 w-3.5" /> Rimuovi
            </Button>
          </div>
        )}

        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-600">{hasKey ? "Sostituisci con nuova chiave" : "Nuova chiave OpenAI *"}</Label>
          <Input
            data-testid="ai-key-input"
            type="password"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            className="mt-2 h-11 font-mono-plex"
            placeholder="sk-proj-..."
            autoComplete="off"
          />
          <div className="mt-2 text-[11px] text-slate-500 leading-relaxed">
            La chiave viene cifrata a riposo (Fernet + JWT_SECRET). Non è mai restituita dal server in chiaro.{" "}
            <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline inline-flex items-center gap-1">
              Ottieni una chiave <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>

        <div className="mt-5">
          <Label className="text-xs uppercase tracking-wider text-slate-600">Modello</Label>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger data-testid="ai-model-select" className="mt-2 h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {availableModels.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="mt-1.5 text-[11px] text-slate-500">
            Consigliato per audit lunghi: <code className="font-mono-plex bg-slate-100 px-1 rounded">gpt-4o</code> (bilanciato) o <code className="font-mono-plex bg-slate-100 px-1 rounded">gpt-4o-mini</code> (economico).
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={save} disabled={saving} data-testid="save-ai-settings-btn" className="h-11 bg-indigo-600 hover:bg-indigo-700 px-6">
          <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva impostazioni"}
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" data-testid="delete-all-btn" className="h-11 text-red-700 hover:bg-red-50 border-red-300 ml-auto">
              <Trash2 className="h-4 w-4" /> Reset totale
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset configurazione AI?</AlertDialogTitle>
              <AlertDialogDescription>Rimuove chiave, modello e prompt personalizzato. I verbali già generati restano salvati.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction onClick={deleteAll} className="bg-red-600 hover:bg-red-700">Reset</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
